import React, { useEffect, useRef, useState } from 'react'
import { withTransaction } from '@elastic/apm-rum-react'
import styled from 'styled-components'
import { Flex, useMatchBreakpoints, Box, Text } from 'vvs-uikit'

import { ContextApi, useTranslation } from 'contexts/Localization'
import useGelatoLimitOrders from 'state/limitOrders/useGelatoLimitOrders'
import Collapse from 'components/Collapse/PrimaryTheme'
import useTheme from 'hooks/useTheme'
import { AppBody } from '../../components/App'

import { useExchangeChartManager } from '../../state/user/hooks'
import MoleTopRow from './components/MoleTopRow'
import PriceChartContainer from './components/Chart/PriceChartContainer'
import { StyledInputCurrencyWrapper, StyledSwapContainer } from './styles'
import CurrencyInputHeader from './components/CurrencyInputHeader'
import LimitOrders from './components/LimitOrders'
import LimitOrdersTable from './components/LimitOrdersTable'

const SwapBody = styled(AppBody)<{ contentHeight: number }>`
  height: ${({ contentHeight }) => `${contentHeight}px`};
  transition: height 300ms ease-in-out;

  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: 18px;
  box-shadow: 0px 10px 50px -20px rgba(0, 0, 0, 0.5);
`
// box-shadow: 2px 2px 20px 2px #FFC700; Glow Effect
// box-shadow: 3px 3px 40px 10px rgba(255, 199, 0, 0.3);

const SwapContent = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  position: relative;
  z-index: 2;
`

const Break = styled.div`
  height: 1px;
  background-color: none;
  width: 100%;
  margin: 16px 0;
`

const CollapseWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  & > div:nth-child(1) {
    border-radius: 18px 18px 18px 18px;
  }

  ${({ theme }) => theme.mediaQueries.xxl} {
    min-height: 740px;
  }
`

const StyledText = styled(Text)`
  > a {
    display: inline;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};

    &:hover {
      text-decoration: underline;
    }
  }
`

const faq: Array<{ [key: string]: (t: ContextApi['t']) => any }> = [
  {
    question: (t) => t('Disclaimer: Limit Orders on VVS Finance are provided by Gelato, a 3rd party protocol. DYOR!'),
    answer: (t) => (
      <Box width="100%">
        <StyledText
          dangerouslySetInnerHTML={{
            __html: t(
              'Limit Orders on VVS Finance are provided by Gelato, a 3rd party protocol and should be considered in beta. DYOR and use at your own risk. VVS Finance is not responsible. More info can be found %urlOpenTag%here.%urlCloseTag%',
              {
                urlOpenTag: '<a href="https://www.gelato.network/" rel="noreferrer">',
                urlCloseTag: '</a>',
              },
            ),
          }}
        />
        <Break />
        <Text>
          {t(
            'In using this service, I understand that small orders are executed at higher execution price due to gas fees costs.',
          )}
        </Text>
        <Break />
        <Text>
          {t(
            'I understand that using taxed tokens on limit orders is dangerous and highly discouraged - Taxed tokens can be stuck in limit orders and lost forever because of impossibility to execute an order for such tokens.',
          )}
        </Text>
      </Box>
    ),
  },
]

function LimitOrdersComponent() {
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()
  const [isChartExpanded] = useState(false)
  const [userChartPreference, setUserChartPreference] = useExchangeChartManager(isMobile)
  const [isChartDisplayed, setIsChartDisplayed] = useState(userChartPreference)
  const { theme } = useTheme()

  useEffect(() => {
    setUserChartPreference(isChartDisplayed)
  }, [isChartDisplayed, setUserChartPreference])

  const contentRef = useRef<HTMLDivElement>()
  const [contentHeight, setContentHeight] = useState(0)
  useEffect(() => {
    if (contentRef && contentRef.current)
      new ResizeObserver(() => {
        if (contentRef && contentRef.current) setContentHeight(contentRef.current.clientHeight)
      }).observe(contentRef.current)
  }, [contentRef])

  const {
    derivedOrderInfo: {
      currencies: limitOrderCurrencies,
      singleTokenPrice: limitOrderSingleTokenPrice,
      currencyIds: limitOrderCurrencyIds,
    },
  } = useGelatoLimitOrders()

  return (
    <Flex
      py="100px"
      px="20px"
      justifyContent="center"
      position="relative"
      flexDirection={isMobile ? 'column-reverse' : 'row'}
      style={{ background: theme.colors.gradients.swapRadial }}
    >
      <PriceChartContainer
        inputCurrencyId={limitOrderCurrencyIds.input}
        inputCurrency={limitOrderCurrencies.input}
        outputCurrencyId={limitOrderCurrencyIds.output}
        outputCurrency={limitOrderCurrencies.output}
        isChartExpanded={isChartExpanded}
        isChartDisplayed={isChartDisplayed}
        currentSwapPrice={limitOrderSingleTokenPrice}
      />
      <Flex flexDirection="column">
        <StyledSwapContainer $isChartExpanded={isChartExpanded}>
          <StyledInputCurrencyWrapper mt={isChartExpanded ? '24px' : '0'} mr={isChartExpanded ? '0' : '0'}>
            <MoleTopRow />
            <SwapBody contentHeight={contentHeight}>
              <SwapContent ref={contentRef}>
                <CurrencyInputHeader title={t('Limit Orders')} setIsChartDisplayed={setIsChartDisplayed} />
                <LimitOrders />
              </SwapContent>
            </SwapBody>
            <LimitOrdersTable />
            <CollapseWrapper mt="20px">
              {faq.map((question) => (
                <Collapse key={question.question(t)} title={question.question(t)}>
                  {question.answer(t)}
                </Collapse>
              ))}
            </CollapseWrapper>
          </StyledInputCurrencyWrapper>
        </StyledSwapContainer>
      </Flex>
    </Flex>
  )
}

export default withTransaction('LimitOrdersComponent', 'component')(LimitOrdersComponent)
